import { useEffect, useState } from "react";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import api from "../../../lib/api";

/**
 * Brief 3.6.5 (B) — Cancel applications modal.
 *
 * Single shared component for both flows:
 *   • Bulk-cancel from /admin/merchants (N selected rows)
 *   • Single-cancel from the merchant detail page (bulk of 1)
 *
 * Hits POST /admin/merchants/cancel with `{merchant_ids, reason}`.
 * Reason gate: ≥20 chars (matches the decline threshold). Server is the
 * source of truth; client just gates the submit button for instant UX.
 *
 * All-or-nothing semantics on the backend → if any merchant is non-
 * cancellable we render the per-row blockers and keep the modal open so
 * the admin can dismiss + adjust their selection.
 */
const REASON_MIN = 20;

export default function CancelMerchantsModal({
  merchants,        // [{merchant_id, company_name?, ref?, status?}, ...]
  onClose,
  onSuccess,        // (cancelResp: {cancelled_count, bulk_action_id, merchant_ids})
  testId = "cancel-merchants-modal",
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [blockers, setBlockers] = useState([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const cleanedReason = reason.trim();
  const trimmedLen = cleanedReason.length;
  const rawLen = reason.length;
  const reasonValid = trimmedLen >= REASON_MIN;
  const whitespaceTrimmed = rawLen - trimmedLen;
  const canSubmit = !submitting && reasonValid && merchants.length > 0;
  const isSingle = merchants.length === 1;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    setBlockers([]);
    try {
      const { data } = await api.post("/admin/merchants/cancel", {
        merchant_ids: merchants.map((m) => m.merchant_id),
        reason: cleanedReason,
      });
      onSuccess?.(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.blockers) {
        setBlockers(detail.blockers);
        setError(detail.message || "Some merchants cannot be cancelled.");
      } else {
        setError(
          status === 422 ? (typeof detail === "string" ? detail : "Reason too short. Minimum 20 characters.") :
          status === 401 ? "Session expired. Please sign in again." :
          "Could not cancel. Please try again."
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
      data-testid={`${testId}-overlay`}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-rose-400/20 bg-[#0a0d12] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Cancel ${merchants.length} merchant${isSingle ? "" : "s"}`}
        data-testid={testId}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-rose-300/70">
              Cancel {isSingle ? "merchant" : "merchants"}
            </div>
            <div className="text-white text-[16px] mt-1">
              {isSingle ? "Soft-cancel this application" : `Cancel ${merchants.length} applications`}
            </div>
            <div className="text-zinc-500 text-[12px] mt-1">
              Cyprus AML 7-year retention — row, documents, and audit trail are preserved.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid={`${testId}-close`}
            className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Selected merchants summary */}
        <div className="mb-4 rounded-xl border border-white/[0.06] bg-white/[0.015] p-3 max-h-44 overflow-y-auto">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
            Will be cancelled
          </div>
          <ul className="space-y-1">
            {merchants.map((m) => (
              <li
                key={m.merchant_id}
                data-testid={`${testId}-row-${m.merchant_id}`}
                className="flex items-center justify-between text-[12.5px]"
              >
                <span className="text-zinc-200 truncate">{m.company_name || m.merchant_id}</span>
                <span className="text-zinc-500 ref-mono text-[11px] ml-3 shrink-0">{m.ref || "—"}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Reason input */}
        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
            Reason — minimum {REASON_MIN} characters
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            data-testid={`${testId}-reason`}
            placeholder='e.g. "Test account from internal QA — not a real prospect."'
            rows={4}
            className="w-full resize-y rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-rose-400/40"
          />
          <div
            className={`mt-1 text-right text-[10.5px] tabular-nums transition-colors ${
              reasonValid ? "text-emerald-300/80" : "text-zinc-500"
            }`}
            data-testid={`${testId}-reason-counter`}
          >
            {trimmedLen} / {REASON_MIN} characters
            {whitespaceTrimmed > 0 && !reasonValid && (
              <span className="ml-2 text-amber-300/80">
                (leading/trailing whitespace not counted)
              </span>
            )}
          </div>
        </div>

        {/* Blockers (409 response) */}
        {blockers.length > 0 && (
          <div
            className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-3"
            data-testid={`${testId}-blockers`}
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-amber-200 mb-1.5">
              <AlertTriangle className="h-3 w-3" />
              Cannot cancel — all-or-nothing
            </div>
            <ul className="space-y-1 text-[12.5px] text-amber-100/90">
              {blockers.map((b, i) => (
                <li key={i} data-testid={`${testId}-blocker-${b.merchant_id}`}>
                  • {b.message}
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[11.5px] text-amber-200/70">
              Remove the listed rows from your selection and retry.
            </div>
          </div>
        )}

        {error && blockers.length === 0 && (
          <div
            className="mb-4 rounded-xl border border-rose-400/20 bg-rose-400/[0.05] px-3 py-2 text-[12.5px] text-rose-200"
            data-testid={`${testId}-error`}
          >
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid={`${testId}-cancel-btn`}
            className="rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.05] text-zinc-200 px-4 py-2 text-[12.5px] transition-colors disabled:opacity-40"
          >
            Back
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid={`${testId}-confirm-btn`}
            className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/40 bg-rose-400/[0.10] hover:bg-rose-400/[0.20] text-rose-100 px-4 py-2 text-[12.5px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitting ? "Cancelling…" : `Cancel ${merchants.length} merchant${isSingle ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
